import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import {
  History, X, ArrowRight, Pencil, Plus, Trash2, Layers,
  User as UserIcon, Loader2,
} from "lucide-react";

const ACTION_META = {
  create:      { label: "Created",      Icon: Plus,    tone: "#065F46", bg: "#ECFDF5" },
  update:      { label: "Edited",       Icon: Pencil,  tone: "#1E40AF", bg: "#EFF6FF" },
  delete:      { label: "Deleted",      Icon: Trash2,  tone: "#991B1B", bg: "#FEF2F2" },
  bulk_update: { label: "Bulk edited",  Icon: Layers,  tone: "#5B21B6", bg: "#F5F3FF" },
  bulk_delete: { label: "Bulk deleted", Icon: Layers,  tone: "#9A3412", bg: "#FFF7ED" },
};

const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return <em className="opacity-50 text-xs">empty</em>;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") {
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
};

/**
 * Side-drawer showing the audit history for a single entity.
 * Fetches `/api/audit-logs?entity_type&entity_id` on open; owner/admin only
 * (the backend enforces role — on 403 we gracefully show an "access denied"
 * message so viewers can see why nothing loaded).
 */
export default function EntityHistoryDrawer({
  open,
  onClose,
  entityType,   // "transaction" | "invoice" | "bank_account" | "custom_category"
  entityId,
  entityLabel,  // optional — shown in the drawer header
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !entityType || !entityId) return;
    let active = true;
    setLoading(true);
    setErr("");
    setItems([]);
    api.get("/audit-logs", {
      params: { entity_type: entityType, entity_id: entityId, limit: 50 },
    }).then((r) => {
      if (!active) return;
      setItems(r.data?.items || []);
    }).catch((e) => {
      if (!active) return;
      const detail = e?.response?.data?.detail;
      setErr(e?.response?.status === 403
        ? "You need owner or admin access to view history."
        : (detail || "Could not load history."));
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [open, entityType, entityId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="entity-history-drawer">
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close history"
        className="flex-1 bg-black/40 backdrop-blur-sm"
        data-testid="history-drawer-backdrop"
      />
      {/* Panel */}
      <aside
        className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col"
        style={{ borderLeft: "1px solid var(--ll-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 sm:px-5 py-3 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
              <History size={11} /> History
            </div>
            <div className="font-display font-bold text-base leading-tight truncate" title={entityLabel}>
              {entityLabel || entityId?.slice(0, 10)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="history-drawer-close"
            aria-label="Close"
            className="p-1.5 hover:bg-[var(--ll-mint)]"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[var(--ll-ink-muted)] p-3">
              <Loader2 size={14} className="animate-spin" /> Loading history…
            </div>
          )}
          {!!err && !loading && (
            <div className="p-3 border bg-amber-50 border-amber-200 text-amber-900 text-sm" data-testid="history-drawer-error">
              {err}
            </div>
          )}
          {!loading && !err && items.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--ll-ink-muted)]" data-testid="history-drawer-empty">
              <History size={24} className="mx-auto mb-2 opacity-40" />
              No manual changes recorded yet.
            </div>
          )}
          <ol className="relative" data-testid="history-drawer-list">
            {items.map((it) => {
              const meta = ACTION_META[it.action] || ACTION_META.update;
              const Icon = meta.Icon;
              return (
                <li key={it.id} className="relative pl-8 pb-4 border-l last:border-l-transparent ml-3" style={{ borderColor: "var(--ll-border)" }}>
                  <div
                    className="absolute -left-[11px] w-5 h-5 grid place-items-center"
                    style={{ background: meta.bg, color: meta.tone, border: `1px solid ${meta.tone}` }}
                  >
                    <Icon size={10} />
                  </div>
                  <div className="text-xs" style={{ color: meta.tone }}>{meta.label}</div>
                  <div className="text-[11px] text-[var(--ll-ink-muted)] flex items-center gap-1.5 mt-0.5">
                    <UserIcon size={10} />
                    <span className="truncate">{it.user_name || it.user_email || "system"}</span>
                    <span>·</span>
                    <span title={it.timestamp}>{fmtDate(it.timestamp)} {it.timestamp?.slice(11, 16)}</span>
                  </div>
                  {(it.diff?.length || 0) > 0 && (
                    <ul className="mt-1.5 space-y-1">
                      {it.diff.map((d, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 text-[11px]">
                          <span className="text-[10px] uppercase tracking-wider text-[var(--ll-ink-muted)] w-20 shrink-0 pt-0.5 truncate" title={d.field}>{d.field}</span>
                          <span className="inline-block px-1.5 py-0.5 bg-[#FEF2F2] text-[#991B1B] border border-[#FCA5A5] max-w-[110px] truncate">{formatValue(d.before)}</span>
                          <ArrowRight size={9} className="opacity-60 mt-1 shrink-0" />
                          <span className="inline-block px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46] border border-[#86EFAC] max-w-[110px] truncate">{formatValue(d.after)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
    </div>
  );
}
